import { Component, OnInit, inject } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { AccountsService } from '../services/accounts-service.service';
import { TemplateModalComponent } from '../template-modal/template-modal.component';
import { SharedTemplateModalComponent } from '../shared-template-modal/shared-template-modal.component';
import { Fund, Template } from '../../models/account-models';
import { MatSnackBar } from '@angular/material/snack-bar';
import { ClipboardService } from 'ngx-clipboard';
import { SessionStorageService } from '../../services/session-storage.service';
import { WarnDialogComponent } from '../../warn-dialog/warn-dialog.component';
import { GridModule } from '@progress/kendo-angular-grid';
import { MatIconModule } from '@angular/material/icon';
import { DatePipe, JsonPipe, PercentPipe } from '@angular/common';
import { UtilityToolbarComponent } from '../../shared-components/utility-toolbar/utility-toolbar.component';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatButtonModule } from '@angular/material/button';
import { InvestmentWizardService } from '../../investment-wizard/services/investment-wizard-service.service';

@Component({
  selector: 'app-templates',
  templateUrl: './templates.component.html',
  styleUrls: ['./templates.component.scss'],
  imports: [
    UtilityToolbarComponent,
    MatIconModule,
    GridModule,
    JsonPipe,
    PercentPipe,
    DatePipe,
    MatSlideToggleModule,
    MatTooltipModule,
    MatButtonModule,
  ],
})
export class TemplatesComponent implements OnInit {
  private as = inject(AccountsService);
  private iws = inject(InvestmentWizardService);
  private dialog = inject(MatDialog);
  private snacky = inject(MatSnackBar);
  private clippy = inject(ClipboardService);
  private ss = inject(SessionStorageService);

  public templates: Template[] = [];
  public loading = false;
  public pagesize = 10;
  public rights = [];

  ngOnInit() {
    this.rights = this.ss.get('rights');
    this.getTemplates();
    this.clippy.copyResponse$.subscribe(res => {
      if (res.isSuccess) {
        this.snacky.open(`Template Share ID: ${res.content} Copied!`, 'Close', {
          duration: 4000,
        });
      }
    });
  }

  getTemplates() {
    this.loading = true;
    this.iws.getTemplates().subscribe(x => {
      if (x && x.results) {
        this.templates = [
          ...x.results.FirmTemplates,
          ...x.results.UserTemplates,
        ];
      }
      this.loading = false;
    });
  }

  saveTemplates(templates) {
    this.loading = true;
    this.as.updateTemplates(templates).subscribe(x => {
      this.templates = [...x.results.FirmTemplates, ...x.results.UserTemplates];
      this.loading = false;
    });
  }

  deleteTemplate(id) {
    const dialogRef = this.dialog.open(WarnDialogComponent, {
      panelClass: 'warn-dialog',
      data: {
        headline: 'Warning',
        content:
          'Are you sure you would like to delete this template? Template deletion cannot be undone.',
        confirm: 'Delete',
      },
    });
    dialogRef.afterClosed().subscribe(result => {
      if (result === 'continue') {
        this.as.deleteTemplate(id).subscribe(x => {
          this.templates = [
            ...x.results.FirmTemplates,
            ...x.results.UserTemplates,
          ];
          this.loading = false;
        });
      }
    });
  }

  editTemplate(data) {
    const dialogRef = this.dialog.open(TemplateModalComponent, {
      minWidth: '80vw',
      panelClass: 'template-dialog',
      data: data,
      disableClose: true,
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        result.data.edited = new Date();
        this.saveTemplates(result.data);
      }
    });
  }

  addSharedTemplate() {
    const dialogRef = this.dialog.open(SharedTemplateModalComponent, {
      panelClass: 'shared-template-dialog',
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        this.templates = [...result.FirmTemplates, ...result.UserTemplates];
      }
    });
  }

  newTemplate() {
    const dialogRef = this.dialog.open(TemplateModalComponent, {
      minWidth: '80vw',
      panelClass: 'template-dialog',
      disableClose: true,
    });
    dialogRef.afterClosed().subscribe(result => {
      if (result && result !== 'cancel') {
        this.saveNewTemplate(
          result.data.name,
          result.data.funds,
          result.data.program,
          result.data.scope,
          result.data.firmTemplate
        );
      }
    });
  }

  saveNewTemplate(name, selectedFunds: Fund[], prog, shareScope, firm) {
    let length = this.templates.length;

    const template: Template = {
      id: `IWT-${length++}`,
      name: name,
      edited: new Date(),
      created: new Date(),
      funds: selectedFunds,
      program: prog,
      active: new Date(),
      scope: shareScope,
      firmTemplate: firm,
    };

    this.loading = true;
    this.as.saveNewTemplate(template).subscribe(x => {
      this.templates = [...x.results.FirmTemplates, ...x.results.UserTemplates];
      this.loading = false;
    });
  }

  clipboardCopy(ev, value) {
    this.clippy.copy(value);
  }

  sharingChange(ev, template) {
    template.scope = ev.checked ? 'private' : 'off';
    this.saveTemplates(template);
  }
}
